import React, { useEffect } from "react";
import MaskedInput from "react-input-mask";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import styled from "@emotion/styled";
import { Modal } from "../Modal";
import { TextField } from "../TextField";
import { ContactFormSchemaValidationType, contactFormSchema } from "./schema";
import { CONTACT_TYPES_TEXT, TEST_ID_PREFIX } from "./constants";
import type { Contact, ContactType } from "./types";

const ContactInfoModal = ({
  contactType,
  contact,
  isOpen,
  onClose,
  onSave,
  loading = false,
  disableEditEmail = false,
}: ContactInfoModalProps) => {
  const isNewContact = contact === undefined;

  const defaultValues = React.useMemo(
    () => ({
      customerFirstName: contact?.customerFirstName || "",
      customerLastName: contact?.customerLastName || "",
      customerPrimaryPhone: contact?.customerPrimaryPhone || "",
      customerEmail: contact?.customerEmail || "",
    }),
    [contact]
  );

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isValid },
  } = useForm<ContactFormSchemaValidationType>({
    mode: "all",
    resolver: zodResolver(contactFormSchema),
  });

  useEffect(() => {
    reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);

  const onSubmit = (data) => {
    const payload = isNewContact ? data : { ...contact, ...data };
    onSave(payload);
    reset();
    onClose();
  };

  const modalTitle = `${isNewContact ? "Add" : "Edit"} ${
    CONTACT_TYPES_TEXT[contactType]
  }`;

  const testIdPrefix = TEST_ID_PREFIX[contactType];

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      title={modalTitle}
      onClose={onClose}
      primaryButton={{
        text: "Save",
        onClick: handleSubmit(onSubmit),
        disabled: !isDirty || !isValid || loading,
        testId: `${testIdPrefix}-contact-modal-update`,
      }}
      secondaryButton={{
        text: "Cancel",
        onClick: onClose,
        disabled: loading,
        testId: `${testIdPrefix}-contact-modal-cancel`,
      }}
      hideIcon
    >
      <FieldsContainer>
        <TextStyles
          label="*First Name"
          placeholder="First Name"
          isError={!!errors?.customerFirstName}
          helperText={errors?.customerFirstName?.message}
          data-testid={`${testIdPrefix}-contact-modal-first-name-input`}
          {...register("customerFirstName")}
        />
        <TextStyles
          label="*Last Name"
          placeholder="Last Name"
          isError={!!errors?.customerLastName}
          helperText={errors?.customerLastName?.message}
          data-testid={`${testIdPrefix}-contact-modal-last-name-input`}
          {...register("customerLastName")}
        />
        <Controller
          name="customerPrimaryPhone"
          control={control}
          render={({ field }) => {
            return (
              <MaskedInput mask="(999) 999-9999" {...field}>
                <TextStyles
                  label="*Phone Number"
                  isError={!!errors?.customerPrimaryPhone}
                  helperText={errors?.customerPrimaryPhone?.message}
                  type="tel"
                  placeholder="(000)-000-0000"
                  data-testid={`${testIdPrefix}-contact-modal-phone-number-input`}
                />
              </MaskedInput>
            );
          }}
        />
        <EmailTextStyles
          label="*Email"
          placeholder="Email"
          isError={!!errors?.customerEmail}
          helperText={errors?.customerEmail?.message}
          data-testid={`${testIdPrefix}-contact-modal-email-input`}
          disabled={disableEditEmail}
          {...register("customerEmail")}
        />
      </FieldsContainer>
    </Modal>
  );
};

interface ContactInfoModalProps {
  contactType: ContactType;
  contact?: Contact;
  isOpen: boolean;
  onClose: () => void;
  onSave: (contact: Contact) => void;
  loading?: boolean;
  disableEditEmail?: boolean;
}

const TextStyles = styled(TextField)`
  width: 100%;

  .sr-text-label {
    color: var(--tints-off-white-20);
  }
`;

const EmailTextStyles = styled(TextStyles)`
  opacity: ${({ disabled }: { disabled: boolean }) => (disabled ? "0.5" : "1")};
`;

const FieldsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
`;

export type { ContactInfoModalProps };

export { ContactInfoModal };
